/* eslint-disable no-nested-ternary */
import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import PropTypes from 'prop-types';
import uploadIcon from '../home/icons/profileFill.svg';
import crossIcon from '../../../public/assets/cross-circle.svg';
import { getAuthToken, getCurrentOrganization } from '../../utils/helpers';
import { useUser } from '../../contexts/ssr-user-context';
import { fileMaxSizeImage } from './forms/constants';

const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];
const IMAGE_QUALITY = 0.7;
const MAX_WIDTH = 300;
const MAX_HEIGHT = 300;

function buildHeaders() {
  const organization = getCurrentOrganization();
  const token = getAuthToken();
  const headers = new Headers();
  headers.append('organization', organization?.orgId || '2410f924-6cc4-4e88-b490-ffd3bbc40d03');
  headers.append('device-type', '4');
  headers.append('version-code', '22');
  headers.append('account-role', 'student');
  if (token) {
    headers.append('Authorization', `Bearer ${token}`);
  }
  return headers;
}

async function resizeImage(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new window.Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width } = img;
        let { height } = img;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width;
            width = MAX_WIDTH;
          }
        } else if (height > MAX_HEIGHT) {
          width *= MAX_HEIGHT / height;
          height = MAX_HEIGHT;
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            const resizedFile = new File([blob], file.name, { type: file.type });
            resolve(resizedFile);
          },
          file.type,
          IMAGE_QUALITY,
        );
      };
    };
  });
}

export default function ProfilePopUp({
  isOpen, onClose, profilePicture, refetch,
}) {
  const fileInputRef = useRef(null);
  const [preview, setPreview] = useState(profilePicture || '');
  const [isBusy, setIsBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const { user, updateUser } = useUser();

  useEffect(() => {
    setPreview(profilePicture || '');
  }, [profilePicture]);

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const showMessage = (text, type = 'info') => {
    setMessage(text);
    setMessageType(type);
  };

  const handleUploadTrigger = () => {
    showMessage('');
    resetFileInput();
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      showMessage('Please select a JPG or PNG image.', 'error');
      return;
    }

    let fileToUpload = file;
    if (file.size > fileMaxSizeImage) {
      fileToUpload = await resizeImage(file);
    }

    const tempUrl = URL.createObjectURL(fileToUpload);
    setPreview(tempUrl);
    setIsBusy(true);
    try {
      const formData = new FormData();
      formData.append('file', fileToUpload);

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_CORE_API_HOST}/auth/upload-profile-picture`,
        {
          method: 'POST',
          body: formData,
          headers: buildHeaders(),
        },
      );

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const result = await response.json();
      const uploadedUrl = result?.data || '';
      setPreview(uploadedUrl);
      updateUser(() => ({
        ...user,
        profile: {
          ...user.profile,
          accountDetails: {
            ...user.profile?.accountDetails,
            profilePicture: uploadedUrl,
          },
        },
      }));
      showMessage('Profile picture updated successfully.', 'success');
      refetch();
    } catch (err) {
      console.error('Error uploading profile picture', err);
      showMessage('Failed to upload image. Please try again.', 'error');
      setPreview(profilePicture || '');
    } finally {
      URL.revokeObjectURL(tempUrl);
      setIsBusy(false);
      resetFileInput();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center backdrop-blur-sm bg-black/40 z-50 px-4"
      onClick={onClose}
    >
      <div
        className="bg-white shadow-lg max-w-md w-full p-8 relative text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute -top-3 -right-3 rounded-full"
        >
          <Image src={crossIcon} alt="Close" width={34} height={34} className="bg-white rounded-full" />
        </button>

        <div className="flex justify-center mb-4">
          <div className="w-20 h-20 bg-[#CEE5FF] rounded-full flex items-center justify-center overflow-hidden">
            {preview ? (
              <Image
                src={preview}
                alt="Profile"
                width={80}
                height={80}
                className="w-20 h-20 object-cover rounded-full"
              />
            ) : (
              <Image src={uploadIcon} alt="Profile" width={50} height={50} />
            )}
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".jpeg,.jpg,.png"
          className="hidden"
          onChange={handleFileChange}
        />

        <p className="text-gray-500 text-base mb-2">Max image: 1GB</p>
        <p className="text-base font-semibold mb-4">Upload your profile image</p>

        {preview ? (
          <button
            type="button"
            className="bg-[#D7F0FF] text-[#045692] font-semibold py-3 px-6 disabled:opacity-70"
            onClick={handleUploadTrigger}
            disabled={isBusy}
          >
            {isBusy ? 'Processing...' : 'CHANGE'}
          </button>
        ) : (
          <button
            type="button"
            className="bg-[#045692] text-white font-semibold py-3 px-6 mx-auto block disabled:opacity-70"
            onClick={handleUploadTrigger}
            disabled={isBusy}
          >
            {isBusy ? 'UPLOADING...' : 'UPLOAD FILES'}
          </button>
        )}

        {message && (
          <p
            className={`mt-4 text-base ${
              messageType === 'error'
                ? 'text-red-600'
                : messageType === 'success'
                  ? 'text-green-600'
                  : 'text-gray-600'
            }`}
          >
            {message}
          </p>
        )}

        <p className=" text-sm mt-3">You can change your profile picture</p>
      </div>
    </div>
  );
}

ProfilePopUp.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  profilePicture: PropTypes.string,
  refetch: PropTypes.func.isRequired,
};
